import React, { FC, KeyboardEvent, ReactNode } from "react";
import Button from "components/Button/Button";
import Icon from "components/Icon/Icon";
import { ButtonColor, ButtonType, ButtonVariant } from "enums/Button";
import { IconType } from "enums/Icon";
import { List } from "immutable";
import { useTranslation } from "react-i18next";
import {
  InfoText,
  InfoWrapper,
} from "../../../../components/Addresses/components/AddressForm.styles";
import { useCurrencySymbol } from "../../../../hooks/useCurrencySymbol";
import formatter from "../../../../utils/formatter";
import {
  Currency,
  InfoRow,
  InfoRowLabel,
  QuoteCard,
  QuoteInfo,
  QuoteLine,
  QuotePrice,
  QuoteService,
  SectionButtonWrapper,
  SectionTitle,
} from "./InboundShipmentPanel.styles";

interface InboundQuoteSectionProps {
  // Persisted estimate from the show serializer — a summary without rate_ids.
  estimate: any;
  // Fresh estimate response ({ rates, valid_weight, … }); only it can buy.
  rateOptions: any;
  selectedRateId: string | null;
  onSelectRate: (rateId: string) => void;
  isEstimating: boolean;
  isShipping: boolean;
  isFormDirty: boolean;
  disabled: boolean;
  onGetQuote: () => void;
  onBuyLabel: () => void;
}

const QuoteNote: FC<{ children: ReactNode }> = ({ children }) => (
  <InfoWrapper>
    <Icon type={IconType.Exclamation} />
    <InfoText>{children}</InfoText>
  </InfoWrapper>
);

const InboundQuoteSection: FC<InboundQuoteSectionProps> = ({
  estimate,
  rateOptions,
  selectedRateId,
  onSelectRate,
  isEstimating,
  isShipping,
  isFormDirty,
  disabled,
  onGetQuote,
  onBuyLabel,
}) => {
  const { t } = useTranslation("common");
  const currencySymbol = useCurrencySymbol();

  const quoteButton = (variant: ButtonVariant) => (
    <Button
      type={ButtonType.Button}
      variant={variant}
      color={ButtonColor.Primary}
      isLoading={isEstimating}
      disabled={isEstimating || disabled}
      onClick={onGetQuote}
    >
      {t("inbound.quote.getQuote")}
    </Button>
  );

  const staleWarning = isFormDirty && (
    <QuoteNote>{t("inbound.quote.staleOnEdit")}</QuoteNote>
  );

  if (!rateOptions && !estimate) {
    return (
      <SectionButtonWrapper>
        {quoteButton(ButtonVariant.Filled)}
      </SectionButtonWrapper>
    );
  }

  const rates = rateOptions ? rateOptions.get("rates", List()) : List();

  if (rateOptions && rates.size === 0) {
    return (
      <div>
        <SectionTitle>{t("inbound.quote.title")}</SectionTitle>
        <QuoteNote>{t("inbound.quote.noRates")}</QuoteNote>
        <SectionButtonWrapper>
          {quoteButton(ButtonVariant.Filled)}
        </SectionButtonWrapper>
      </div>
    );
  }

  // Persisted estimate but no fresh rates (e.g. a returning session): show
  // the stored summary; buying needs a fresh estimate because rate_ids are
  // only returned by the estimate call and expire on the carrier side.
  if (!rateOptions) {
    return (
      <div>
        <SectionTitle>{t("inbound.quote.title")}</SectionTitle>
        {staleWarning}
        <QuoteCard>
          <QuoteInfo>
            <QuoteService>{estimate.get("service_type")}</QuoteService>
            <QuoteLine>
              {t("inbound.quote.shipping")}: {currencySymbol}
              {formatter.currency(estimate.get("shipping_total"))}
            </QuoteLine>
            {Number(estimate.get("volumetric_extra_charge")) > 0 && (
              <QuoteLine>
                {t("inbound.quote.volumetricExtra")}: {currencySymbol}
                {formatter.currency(estimate.get("volumetric_extra_charge"))}
              </QuoteLine>
            )}
          </QuoteInfo>
          <QuotePrice>
            <Currency>{currencySymbol}</Currency>{" "}
            {formatter.currency(estimate.get("total"))}
          </QuotePrice>
        </QuoteCard>
        <QuoteNote>{t("inbound.quote.expiredNote")}</QuoteNote>
        <SectionButtonWrapper>
          {quoteButton(ButtonVariant.Filled)}
        </SectionButtonWrapper>
      </div>
    );
  }

  // While the detail record is loading or a purchase is in flight the whole
  // section is locked — the cards must not change the selection either.
  const isLocked = disabled || isShipping;
  const selectedIndex = rates.findIndex(
    (rate: any) => rate.get("rate_id") === selectedRateId,
  );

  return (
    <div>
      <SectionTitle>{t("inbound.quote.title")}</SectionTitle>
      {staleWarning}
      <div role="radiogroup" aria-label={t("inbound.quote.title")}>
        {rates.map((rate: any, index: number) => {
          const rateId = rate.get("rate_id");
          const isSelected = rateId === selectedRateId;
          // Roving tabindex: one tab stop for the whole group — the selected
          // card, or the first one while nothing is selected.
          const isTabStop =
            selectedIndex === -1 ? index === 0 : isSelected;
          return (
            <QuoteCard
              key={rateId}
              role="radio"
              aria-checked={isSelected}
              aria-disabled={isLocked}
              tabIndex={isTabStop ? 0 : -1}
              $isSelected={isSelected}
              $isClickable={!isLocked}
              onClick={() => {
                if (!isLocked) onSelectRate(rateId);
              }}
              onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
                if (isLocked) return;
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  onSelectRate(rateId);
                  return;
                }
                const forward =
                  event.key === "ArrowDown" || event.key === "ArrowRight";
                const backward =
                  event.key === "ArrowUp" || event.key === "ArrowLeft";
                if (!forward && !backward) return;
                event.preventDefault();
                const nextIndex =
                  (index + (forward ? 1 : -1) + rates.size) % rates.size;
                onSelectRate(rates.getIn([nextIndex, "rate_id"]));
                // selection follows focus in a radio group
                const group = event.currentTarget.parentElement;
                (group?.children[nextIndex] as HTMLElement | undefined)?.focus();
              }}
            >
              <QuoteInfo>
                <QuoteService>
                  {rate.get("carrier_name")} — {rate.get("service_name")}
                </QuoteService>
                {Number(rate.get("delivery_days")) > 0 && (
                  <QuoteLine>
                    {t("inbound.quote.deliveryDays", {
                      amount: rate.get("delivery_days"),
                    })}
                  </QuoteLine>
                )}
              </QuoteInfo>
              <QuotePrice>
                <Currency>{currencySymbol}</Currency>{" "}
                {formatter.currency(rate.get("total_amount"))}
              </QuotePrice>
            </QuoteCard>
          );
        })}
      </div>
      {Number(rateOptions.get("volumetric_extra_charge")) > 0 && (
        <InfoRow>
          <InfoRowLabel>{t("inbound.quote.volumetricExtra")}</InfoRowLabel>
          <span>
            {currencySymbol}
            {formatter.currency(rateOptions.get("volumetric_extra_charge"))}
          </span>
        </InfoRow>
      )}
      {rateOptions.get("valid_weight") === false && (
        <QuoteNote>{t("inbound.quote.volumetricWarning")}</QuoteNote>
      )}
      <SectionButtonWrapper>
        {quoteButton(ButtonVariant.Bordered)}
        <Button
          type={ButtonType.Button}
          variant={ButtonVariant.Filled}
          color={ButtonColor.Primary}
          isLoading={isShipping}
          disabled={isShipping || disabled || !selectedRateId}
          onClick={onBuyLabel}
        >
          {t("inbound.quote.buyLabel")}
        </Button>
      </SectionButtonWrapper>
    </div>
  );
};

export default React.memo(InboundQuoteSection);
